import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Truck, ShieldCheck, Pencil, Trash2, Plus, Flag } from "lucide-react";
import { AppHeader } from "@/components/AppHeader";
import { NameDialog } from "@/components/NameDialog";
import { ConfirmRemoveDialog } from "@/components/ConfirmRemoveDialog";
import { useApi, errMsg, fmtDate, fmtDateTime } from "@/lib/api";

const VehicleCard = ({ sid, v, index, onRename, onDelete }) => {
  const pct = v.total ? Math.round((v.checked / v.total) * 100) : 0;
  const done = !!v.finalized_at;
  const bar = done ? "bg-[#00E676]" : pct > 0 ? "bg-[#FFB300]" : "bg-slate-600";
  return (
    <li className="fade-up relative" style={{ animationDelay: `${index * 60}ms` }}>
      <Link to={`/show/${sid}/v/${v.key}`} data-testid={`vehicle-card-${v.key}`}
        className={`block rounded-xl border p-4 transition-colors hover:bg-[#1C2029] ${done ? "border-[#00E676]/50 bg-[#00E676]/[0.05]" : "border-[#2A2E39] bg-[#15181E] hover:border-[#636b2f]"}`}>
        <div className="flex items-center gap-3 pr-20">
          <div className={`w-12 h-12 shrink-0 rounded-lg grid place-items-center ${done ? "bg-[#00E676]/15 text-[#00E676]" : "bg-[#0B0C10] text-[#A9B26A]"}`}>
            {done ? <ShieldCheck className="w-6 h-6" /> : <Truck className="w-6 h-6" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display text-2xl font-black uppercase tracking-wide text-white leading-none truncate" data-testid={`vehicle-name-${v.key}`}>{v.name}</div>
            <div className="font-mono text-xs text-slate-400 mt-1" data-testid={`vehicle-progress-${v.key}`}>{v.checked}/{v.total} itens · {pct}%</div>
          </div>
        </div>
        <div className="h-1.5 rounded-full bg-[#0B0C10] mt-4 overflow-hidden">
          <div className={`h-full ${bar} transition-[width] duration-500`} style={{ width: `${done ? 100 : pct}%` }} />
        </div>
        {done && (
          <p className="text-xs text-[#00E676] mt-3" data-testid={`vehicle-finalized-by-${v.key}`}>
            Finalizado por {v.finalized_by?.name} · {fmtDateTime(v.finalized_at)}
          </p>
        )}
      </Link>
      <div className="absolute top-5 right-3 flex">
        <button type="button" onClick={() => onRename(v)} data-testid={`vehicle-rename-btn-${v.key}`} title="Renomear" className="w-9 h-9 grid place-items-center rounded-md text-slate-500 hover:text-[#A9B26A] hover:bg-[#636b2f]/20 transition-colors">
          <Pencil className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => onDelete(v)} data-testid={`vehicle-delete-btn-${v.key}`} title="Remover" className="w-9 h-9 grid place-items-center rounded-md text-slate-500 hover:text-[#FF3838] hover:bg-[#FF3838]/10 transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
};

export default function ShowPage() {
  const { sid } = useParams();
  const api = useApi();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    api.get(`/shows/${sid}`).then((r) => setShow(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [api, sid]);

  const run = async (fn, okMsg) => {
    try { const r = await fn(); setShow(r.data); toast.success(okMsg); return true; }
    catch (e) { toast.error(errMsg(e)); return false; }
  };

  const done = show ? show.vehicles.filter((v) => v.finalized_at).length : 0;
  return (
    <div className="min-h-screen pb-10">
      <AppHeader back="/" title={show?.city || "Carregando..."} subtitle={show ? [show.venue, fmtDate(show.date)].filter(Boolean).join(" · ") : ""} />
      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {show && (
          <div className="flex items-end justify-between">
            <h1 className="font-display text-4xl font-black uppercase text-white leading-none">Frota</h1>
            <span className="font-mono text-sm text-slate-400" data-testid="show-finalized-count">{done}/{show.vehicles.length} finalizados</span>
          </div>
        )}
        {!show ? (
          <div className="space-y-3">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-28 rounded-xl shimmer" />)}</div>
        ) : (
          <>
            <ul className="grid sm:grid-cols-2 gap-3" data-testid="vehicles-list">
              {show.vehicles.map((v, i) => <VehicleCard key={v.key} sid={sid} v={v} index={i} onRename={setRenaming} onDelete={setRemoving} />)}
            </ul>
            <button type="button" onClick={() => setAdding(true)} data-testid="add-vehicle-btn"
              className="w-full min-h-[52px] rounded-xl border border-dashed border-[#2A2E39] text-slate-400 hover:text-[#A9B26A] hover:border-[#636b2f] flex items-center justify-center gap-2 font-semibold transition-colors">
              <Plus className="w-5 h-5" /> Adicionar caminhão
            </button>
            <button type="button" onClick={() => navigate("/")} data-testid="finish-show-btn"
              className="w-full h-14 rounded-md bg-[#636b2f] text-white hover:bg-[#737c37] flex items-center justify-center gap-2 font-display text-lg font-black uppercase tracking-wider transition-colors">
              <Flag className="w-5 h-5" /> Finalizar Show
            </button>
          </>
        )}
      </main>
      <NameDialog open={adding} onOpenChange={setAdding} testId="add-vehicle" title="Novo caminhão" label="Nome" initial=""
        submitLabel="Adicionar" templateLabel="Incluir também nos próximos shows"
        onSubmit={({ name, template }) => run(() => api.post(`/shows/${sid}/vehicles`, { name, template }), "Caminhão adicionado")} />
      <NameDialog open={!!renaming} onOpenChange={(o) => !o && setRenaming(null)} testId="rename-vehicle" title="Renomear" label="Nome"
        initial={renaming?.name || ""} submitLabel="Salvar" templateLabel="Usar esse nome também nos próximos shows"
        onSubmit={({ name, template }) => run(() => api.patch(`/shows/${sid}/vehicles/${renaming.key}`, { name, template }), "Nome atualizado")} />
      <ConfirmRemoveDialog open={!!removing} testId="delete-vehicle" title={`Remover ${removing?.name}?`}
        description="O veículo, seus itens e fotos saem do checklist deste show."
        onCancel={() => setRemoving(null)}
        onConfirm={(fromTemplate) => { run(() => api.delete(`/shows/${sid}/vehicles/${removing.key}`, { params: { remove_from_template: fromTemplate } }), "Caminhão removido"); setRemoving(null); }} />
    </div>
  );
}
